import { Medal07Icon } from '@hugeicons/react'
import clsx from 'clsx'

import { MedalThumb } from '@/features/medals/components/MedalThumb'
import { useMedalsCollection } from '@/features/medals/hooks/useMedalsCollection'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { Card } from '@/shared/ui/Card'
import { SectionHeader } from '@/shared/ui/Layout'
import { ProgressBar } from '@/shared/ui/ProgressBar'

import { BlockEmptyState } from '../blocks/BlockEmptyState'
import { BlockSkeleton } from '../blocks/BlockSkeleton'
import { BlockGuidanceSheet } from '../blocks/guidance/ui/BlockGuidanceSheet'
import { GuidanceDoor } from '../blocks/guidance/ui/GuidanceDoor'
import { useBlockGuidance } from '../blocks/guidance/useBlockGuidance'

import type { BlockComponentProps } from '../blocks/index'

const PREVIEW_SIZE = 6

export const MedalsPreview = ({ block }: Partial<BlockComponentProps>) => {
  const { navigateTo } = useNavigation()
  const { medals, unlockedCount, total, percent, nextMedal, isEmpty, isLoading } =
    useMedalsCollection()
  const guidance = useBlockGuidance('medals')

  if (isLoading) return <BlockSkeleton rows={2} />

  // O bloco não some mais quando não há medalhas: o produtor precisa enxergar
  // na home o bloco que posicionou no editor.
  //
  // Sem `action`: vazio de configuração não tem regra a explicar, e uma porta
  // que abre para "não há o que fazer" é pior que nenhuma porta.
  if (isEmpty) {
    return (
      <BlockEmptyState
        icon={Medal07Icon}
        title="Nenhuma medalha ainda"
        hint="As medalhas do evento aparecem aqui assim que forem publicadas."
      />
    )
  }

  const preview = medals.slice(0, block?.settings?.limit ?? PREVIEW_SIZE)

  return (
    <Card className="card-gamification">
      <div className="px-4 pb-0.5 pt-3">
        {/* Ícone sólido `stroke` em vez de `duotone` âmbar: cor de ícone de
            header não é decisão por widget. */}
        <SectionHeader
          icon={<Medal07Icon size={16} variant="stroke" aria-hidden />}
          title="Minhas medalhas"
        >
          <SectionHeader.Action onClick={() => navigateTo('/medalhas')}>
            Ver coleção
          </SectionHeader.Action>
        </SectionHeader>
      </div>

      <Card.Body className="flex flex-col gap-2.5 px-4 pb-3 pt-1.5">
        {/*
          Aqui ficava o texto fixo "Complete jornadas para desbloquear
          medalhas." — errado em todo evento cujas medalhas vêm de missão,
          porque `Medal` aceita jornada **ou** missão como origem. O que sobrou
          é status; a explicação virou a ajuda no rodapé.
        */}
        {unlockedCount === 0 && (
          <p className="m-0 py-1 text-xs font-semibold text-gray-700">
            Você ainda não conquistou medalhas.
          </p>
        )}

        <div className="grid grid-cols-3 gap-2">
          {preview.map(medal => (
            <div key={medal.id} className="flex flex-col items-center gap-1">
              <MedalThumb medal={medal} size="sm" />
              <span
                className={clsx(
                  'line-clamp-2 text-center text-2xs font-semibold leading-tight',
                  medal.unlocked ? 'text-gray-800' : 'text-gray-700'
                )}
              >
                {medal.name}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">
              {unlockedCount} de {total} desbloqueadas
            </span>
            <span className="text-2xs font-bold text-gray-700">
              {percent}%
            </span>
          </div>
          <ProgressBar value={percent} variant="primary" size="sm" />
        </div>

        {nextMedal && unlockedCount > 0 && (
          <p className="m-0 text-2xs font-semibold text-gray-700">
            Próxima medalha:{' '}
            <span className="font-bold text-gray-900">{nextMedal.name}</span>
          </p>
        )}
      </Card.Body>

      {/*
        Ajuda no rodapé, não no topo do corpo: a porta herdou o lugar da linha
        de texto que substituiu, e ali ela empurrava a grade para baixo e lia
        como aviso. No rodapé é afordância de ajuda — mesmo idioma do CTA de
        rodapé do `ObjectivesCard` (borda superior, largura total, `py-3`), que
        também entrega alvo de toque com folga.
      */}
      {unlockedCount === 0 && guidance.hasGuidance && (
        <GuidanceDoor label={guidance.doorLabel} onClick={guidance.onOpen} />
      )}

      <BlockGuidanceSheet
        open={guidance.open}
        onClose={guidance.onClose}
        title={guidance.sheetTitle}
        actions={guidance.actions}
      />
    </Card>
  )
}
